// App.js
import React, { useState, useEffect, createContext } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './Login';
import Signup from './Signup';
import ResetPassword from './ResetPassword';
import DashboardEntry from './DashboardEntry';
import Coinflip from './Coinflip';
import Admin from './admin';
import API from './api';
import UserStatsModal from './UserStatsModal';
import Deposit from './DepositForm';
import Withdraw from './WithdrawForm';
import LimboGame from './LimboGame';
// JackpotGame removed for maintenance redirect logic in routes
import ProvablyFair from './ProvablyFair';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import AMLPolicy from './AMLPolicy';
import FAQ from './FAQ';
import CookiePolicy from './CookiePolicy';
import RedeemCodeModal from './RedeemCodeModal';
import MaintenancePage from './MaintenancePage';
import AnnouncementBanner from './AnnouncementBanner';
import AuthCallback from './AuthCallback';
import AuthModal from './AuthModal';
import VerifyEmail from './VerifyEmail';
import Cases from './Cases';
import CaseBattles from './CaseBattles';
import BustGame from './BustGame';



import LayoutWrapper from './LayoutWrapper';

import { ChatProvider } from './ChatContext';
import ToastNotifications from './ToastNotifications';
import './index.css';

export const UserContext = createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const navigate = useNavigate();
  
  // NEW: System state
  const [maintenance, setMaintenance] = useState({ enabled: false });
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.removeItem('user');
      setUser(null);
      setLoading(false);
      return;
    }

    async function fetchUser() {
      try {
        console.log('Fetching user with token:', token.substring(0, 20) + '...');
        const res = await API.get('/auth/me');
        
        const freshUserData = {
          username: res.data.username,
          balance: res.data.balance,
          promoBalance: res.data.promoBalance || 0,
          id: res.data.id || res.data._id,
          totalWagered: res.data.totalWagered || 0,
          referralCode: res.data.referralCode,
          referralEarnings: res.data.referralEarnings || 0,
          referralCount: res.data.referralCount || 0,
          roles: res.data.roles || [],
          isAdmin: res.data.roles?.includes('admin') || res.data.isAdmin,
          avatar: res.data.avatar || '',
          email: res.data.email || '',
          emailVerified: res.data.emailVerified || false,
          unwageredAmount: res.data.unwageredAmount || 0,
          isChatMuted: res.data.isChatMuted || false,
          muteReason: res.data.muteReason || null,
          referralCodeChanged: res.data.referralCodeChanged || false,
          discordConnected: res.data.discordConnected || false,
          discordId: res.data.discordId || null,
          level: res.data.level || { current: 1, progress: 0, totalWagered: 0 },
          stats: res.data.stats || {}
        };
        
        console.log('Fresh user data from server:', freshUserData);
        
        setUser(freshUserData);
        localStorage.setItem('user', JSON.stringify(freshUserData));
        
      } catch (err) {
        console.error('Failed to fetch user:', err);
        // Only log out if it's explicitly an unauthorized error
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    }

    // Fetch system status
    async function fetchSystemStatus() {
      try {
        console.log('[DEBUG] Fetching system status...');
        const res = await API.get('/system/status');
        console.log('[DEBUG] System status response:', res.data);
        setMaintenance(res.data.maintenance);
        setAnnouncement(res.data.announcement);
      } catch (err) {
        console.error('[DEBUG] Failed to fetch system status:', err);
      }
    }

    fetchUser();
    fetchSystemStatus();

    // Handle system events
    const handleMaintenance = (e) => {
      console.log('[DEBUG] Maintenance event received:', e.detail);
      setMaintenance(e.detail.enabled);
    };

    const handleAnnouncement = (e) => {
      console.log('[DEBUG] Announcement event received:', e.detail);
      // Only set announcement if it's explicitly active in the event data
      if (e.detail && e.detail.active) {
        setAnnouncement(e.detail);
      } else {
        setAnnouncement(null);
      }
    };

    const handleForceLogout = (e) => {
      alert(`Session ended: ${e.detail?.reason || 'No reason specified'}`);
      forceLogout();
      // Use navigate instead of location.href to avoid hard reloads
      navigate('/dashboard');
    };

    const handleNotification = (e) => {
      if (window._addToast) {
        window._addToast({
          type: e.detail.type || 'info',
          title: e.detail.title || 'Notification',
          message: e.detail.message || '',
        });
      }
    };

    const handleBalanceUpdate = (e) => {
      const data = e.detail;
      if (data && setUser) {
        // IGNORE socket updates if we are in the middle of a case opening or battle animation
        // The game component will dispatch its own final update at the end of the animation
        if ((window._isCaseOpening || window._isBattleOpening) && !data.isFinal) {
            console.log('[DEBUG] Balance update buffered/ignored due to active animation');
            window._pendingBalanceUpdate = data;
            return;
        }
        window._pendingBalanceUpdate = null;

        // Try both balance and newBalance keys for compatibility with various backend emitters
        const newBal = data.balance !== undefined ? data.balance : data.newBalance;
        const newPromo = data.promoBalance !== undefined ? data.promoBalance : data.newPromoBalance;

        setUser(prev => {
          if (!prev) return prev;
          const updatedUser = {
            ...prev,
            ...(newBal !== undefined ? { balance: parseFloat(newBal) } : {}),
            ...(newPromo !== undefined ? { promoBalance: parseFloat(newPromo) } : {})
          };
          // Sync with localStorage
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      }
    };

    const handleNewTip = (e) => {
      if (window._addToast) {
        window._addToast({
          type: 'success',
          title: 'New Tip!',
          message: e.detail.message || `You received a tip!`
        });
      }
    };

    const handleLevelUp = (e) => {
      if (window._addToast) {
        window._addToast({
          type: 'success',
          title: 'LEVEL UP!',
          message: `Congratulations! You reached level ${e.detail.newLevel}!`
        });
      }
    };

    window.addEventListener('maintenanceStatus', handleMaintenance);
    window.addEventListener('announcementUpdate', handleAnnouncement);
    window.addEventListener('forceLogout', handleForceLogout);
    window.addEventListener('notification', handleNotification);
    window.addEventListener('balanceUpdate', handleBalanceUpdate);
    window.addEventListener('newTip', handleNewTip);
    window.addEventListener('levelUp', handleLevelUp);

    return () => {
      window.removeEventListener('maintenanceStatus', handleMaintenance);
      window.removeEventListener('announcementUpdate', handleAnnouncement);
      window.removeEventListener('forceLogout', handleForceLogout);
      window.removeEventListener('notification', handleNotification);
      window.removeEventListener('balanceUpdate', handleBalanceUpdate);
      window.removeEventListener('newTip', handleNewTip);
      window.removeEventListener('levelUp', handleLevelUp);
    };
  }, []);

  const [activeBalance, setActiveBalance] = useState('balance');

  const forceLogout = () => {
    console.log('Force logout - clearing all session data');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) return <div>Loading...</div>;

  const isMaintenanceDisabled = user?.isAdmin || user?.roles?.includes('admin');

  // Hard-force maintenance lockdown for non-admins (DISABLED so it doesn't block regular Cases)
  /*
  if (maintenance?.enabled && !isMaintenanceDisabled && !loading) {
    return (
      <UserContext.Provider value={{ 
        user, setUser, forceLogout, 
        activeBalance, setActiveBalance 
      }}>
        <ChatProvider currentUser={user}>
          <MaintenancePage maintenance={maintenance} />
          <div style={{ position: 'fixed', top: 0, zIndex: 100000, width: '100%' }}>
            <AnnouncementBanner 
              announcement={announcement} 
              onClose={() => setAnnouncement(null)} 
            />
          </div>
        </ChatProvider>
      </UserContext.Provider>
    );
  }
  */

  return (
    <UserContext.Provider value={{ 
      user, setUser, forceLogout, 
      activeBalance, setActiveBalance,
      isRedeemModalOpen, setIsRedeemModalOpen,
      isDepositModalOpen, setIsDepositModalOpen,
      isWithdrawModalOpen, setIsWithdrawModalOpen,
      isAuthModalOpen, setIsAuthModalOpen,
      authModalView, setAuthModalView
    }}>
      <ChatProvider currentUser={user}>
        {/* Global Announcement Banner */}
        <div style={{ position: 'relative', zIndex: 100000 }}>
          <AnnouncementBanner 
            announcement={announcement} 
            onClose={() => setAnnouncement(null)} 
          />
        </div>

        <Routes>
          {/* Redirect root to dashboard for ALL users */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Dashboard handles both logged-in and logged-out states */}
          <Route path="/dashboard" element={<LayoutWrapper><DashboardEntry /></LayoutWrapper>} />
          <Route path="/dashboard/:tab" element={<LayoutWrapper><DashboardEntry /></LayoutWrapper>} />
          
          {/* Redirect legacy auth routes to dashboard home */}
          <Route path="/login" element={<Navigate to="/dashboard?tab=home" replace />} />
          <Route path="/signup" element={<Navigate to="/dashboard?tab=home" replace />} />
          <Route path="/reset-password" element={<LayoutWrapper><ResetPassword /></LayoutWrapper>} />
          <Route path="/auth/callback" element={<LayoutWrapper><AuthCallback /></LayoutWrapper>} />
          
          {/* Game routes - accessible logged out, but blocked in-game */}
          <Route path="/coinflip" element={<LayoutWrapper><Coinflip /></LayoutWrapper>} />
          <Route path="/jackpot" element={
            <div ref={(el) => {
              if (el) {
                if (window._addToast) {
                  window._addToast({
                    type: 'error',
                    title: 'Maintenance',
                    message: 'Jackpot is currently under maintenance. Please try again later.'
                  });
                }
                navigate('/');
              }
            }} />
          } />
          <Route path="/cases" element={<LayoutWrapper><Cases /></LayoutWrapper>} />
          <Route path="/case-battles" element={<LayoutWrapper><CaseBattles /></LayoutWrapper>} />
          <Route path="/LimboGame" element={<LayoutWrapper><LimboGame /></LayoutWrapper>} />

          <Route path="/bust" element={<LayoutWrapper><BustGame /></LayoutWrapper>} />
          
          {/* Protected financial routes */}
          <Route path="/DepositForm" element={user ? <LayoutWrapper><Deposit /></LayoutWrapper> : <Navigate to="/dashboard?tab=home" />} />
          <Route path="/WithdrawForm" element={user ? <LayoutWrapper><Withdraw /></LayoutWrapper> : <Navigate to="/dashboard?tab=home" />} />
          
          <Route path="/verify-email" element={<LayoutWrapper><VerifyEmail /></LayoutWrapper>} />
          <Route path="/fairness" element={<LayoutWrapper><ProvablyFair /></LayoutWrapper>} />
          <Route path="/tos" element={<Navigate to="/fairness?tab=tos" replace />} />
          <Route path="/privacy" element={<Navigate to="/fairness?tab=privacy" replace />} />
          <Route path="/aml" element={<Navigate to="/fairness?tab=aml" replace />} />
          <Route path="/faq" element={<Navigate to="/fairness?tab=faq" replace />} />
          <Route path="/cookies" element={<Navigate to="/fairness?tab=cookies" replace />} />
          
          {/* Admin route - restricted to admin users only */}
          <Route
            path="/admin"
            element={(user?.isAdmin || user?.roles?.includes('admin'))
              ? <LayoutWrapper><Admin /></LayoutWrapper>
              : <Navigate to="/dashboard?tab=home" />
            }
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <ToastNotifications />
        <UserStatsModal />
        <RedeemCodeModal 
          isOpen={isRedeemModalOpen} 
          onClose={() => setIsRedeemModalOpen(false)} 
        />

        {user && isDepositModalOpen && (
          <Deposit isOpen={isDepositModalOpen} onClose={() => setIsDepositModalOpen(false)} />
        )}
        {user && isWithdrawModalOpen && (
          <Withdraw isOpen={isWithdrawModalOpen} onClose={() => setIsWithdrawModalOpen(false)} />
        )}
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={() => setIsAuthModalOpen(false)} 
          initialView={authModalView}
        />
      </ChatProvider>
    </UserContext.Provider>
  );
}

export default App;
